import { Breadcrumb, Spin, Form, Input, Button, Checkbox, Alert } from "antd";
import { showError, showSuccess } from "common/ToastNotifications";
import AppPaths from "constants/appPaths";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";
import { HomeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import _ from "lodash";
import { deriveColumnsAtStep } from "components/organisms/PipelineBuilder/deriveColumns";

const { TextArea } = Input;

class CreateEditViewPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            saving: false,
            isEdit: false,
            isDuplicate: false,
            schemaFields: [],
            loadingSchema: false,
            formData: {
                viewName: "",
                description: "",
                pipelineId: null,
                sourceDatasetId: "",
                sourceSchemaId: "",
                businessAreaId: "",
                columnConfig: { visible: [], hidden: [] },
            },
        };
    }

    componentDidMount() {
        const viewId = this.props.match.params.viewId;
        if (viewId) {
            this.setState({ isEdit: true, loading: true });
            this.props.getViewById(viewId);
        } else {
            const duplicate = this.props.location?.state?.duplicate;
            if (duplicate) {
                this.setState({ isDuplicate: true, formData: { ...this.state.formData, ...duplicate } });
                if (duplicate.businessAreaId && duplicate.sourceSchemaId) {
                    this.fetchSchemaFields(duplicate.businessAreaId, duplicate.sourceSchemaId);
                }
                if (duplicate.pipelineId) {
                    this.props.getPipelineById(duplicate.pipelineId);
                }
            }
        }
    }

    componentDidUpdate(prevProps) {
        // Handle view loaded for editing
        if (prevProps.getViewByIdResult !== this.props.getViewByIdResult) {
            this.setState({ loading: false });
            if (this.props.viewDetail && this.state.isEdit) {
                const view = this.props.viewDetail;
                this.setState({
                    formData: {
                        viewName: view.viewName,
                        description: view.description || "",
                        pipelineId: view.pipelineId,
                        sourceDatasetId: view.sourceDatasetId,
                        sourceSchemaId: view.sourceSchemaId,
                        businessAreaId: view.businessAreaId,
                        columnConfig: view.columnConfig || { visible: [], hidden: [] },
                    },
                });
                if (view.businessAreaId && view.sourceSchemaId) {
                    this.fetchSchemaFields(view.businessAreaId, view.sourceSchemaId);
                }
                if (view.pipelineId) {
                    this.props.getPipelineById(view.pipelineId);
                }
            }
        }

        // Handle schema model loaded
        if (prevProps.getSchemaModelResult !== this.props.getSchemaModelResult) {
            if (this.props.schemaModel && this.props.schemaModel.fields) {
                this.setState({ loadingSchema: false }, this.recomputeEffectiveFields);
            } else {
                this.setState({ loadingSchema: false });
                if (this.props.getSchemaModelResult && this.props.getSchemaModelResult.success === false) {
                    showError("Failed to load schema fields");
                }
            }
        }

        // Handle pipeline loaded — recompute effective fields after transforms
        if (prevProps.currentPipeline !== this.props.currentPipeline) {
            this.recomputeEffectiveFields();
        }

        // Handle create result
        if (prevProps.createViewResult !== this.props.createViewResult) {
            this.setState({ saving: false });
            if (this.props.createViewResult && this.props.createViewResult.success) {
                showSuccess("View created successfully");
                this.onBack();
            } else if (this.props.createViewResult && this.props.createViewResult.success === false) {
                showError("Failed to create view");
            }
        }

        // Handle update result
        if (prevProps.updateViewResult !== this.props.updateViewResult) {
            this.setState({ saving: false });
            if (this.props.updateViewResult && this.props.updateViewResult.success) {
                showSuccess("View updated successfully");
                this.onBack();
            } else if (this.props.updateViewResult && this.props.updateViewResult.success === false) {
                showError("Failed to update view");
            }
        }
    }

    onBack = () => {
        const tenant = this.props.tenant;
        if (tenant) {
            this.props.history.push(AppPaths.TENANT_VIEWS.replace(":tenant", tenant.name));
        }
    };

    onSave = () => {
        const { formData, isEdit } = this.state;

        if (!formData.viewName.trim()) {
            showError("View name is required");
            return;
        }
        if (!isEdit) {
            if (!formData.sourceDatasetId.trim()) {
                showError("Source Dataset ID is required");
                return;
            }
            if (!formData.sourceSchemaId.trim()) {
                showError("Source Schema ID is required");
                return;
            }
            if (!formData.businessAreaId.trim()) {
                showError("Business Area ID is required");
                return;
            }
        }
        if (formData.columnConfig.visible.length === 0) {
            showError("At least one column must be selected as visible");
            return;
        }

        this.setState({ saving: true });

        if (isEdit) {
            const viewId = this.props.match.params.viewId;
            this.props.updateView(viewId, {
                viewName: formData.viewName,
                description: formData.description,
                pipelineId: formData.pipelineId,
                columnConfig: formData.columnConfig,
                version: this.props.viewDetail.version,
            });
        } else {
            this.props.createView(formData);
        }
    };

    onFieldChange = (field, value) => {
        this.setState((prev) => ({
            formData: { ...prev.formData, [field]: value },
        }));

        // Auto-fetch schema fields when both businessAreaId and sourceSchemaId are set
        if (field === "sourceSchemaId" || field === "businessAreaId") {
            const businessAreaId = field === "businessAreaId" ? value : this.state.formData.businessAreaId;
            const sourceSchemaId = field === "sourceSchemaId" ? value : this.state.formData.sourceSchemaId;
            if (businessAreaId && sourceSchemaId) {
                this.debouncedFetchSchemaFields(businessAreaId, sourceSchemaId);
            }
        }
    };

    debouncedFetchSchemaFields = _.debounce((businessAreaId, schemaId) => {
        this.fetchSchemaFields(businessAreaId, schemaId);
    }, 500);

    fetchSchemaFields = (businessAreaId, schemaId) => {
        this.setState({ loadingSchema: true, schemaFields: [] });
        this.props.getSchemaModel(businessAreaId, schemaId);
    };

    componentWillUnmount() {
        this.debouncedFetchSchemaFields.cancel();
    }

    recomputeEffectiveFields = () => {
        const schemaModel = this.props.schemaModel;
        if (!schemaModel || !schemaModel.fields) return;

        const sourceFields = schemaModel.fields;
        const nameToSourceField = {};
        for (const f of sourceFields) {
            nameToSourceField[f.name] = f;
        }

        // Get pipeline steps (current pipeline if loaded, else snapshot on view)
        const pipeline = this.props.currentPipeline;
        const view = this.props.viewDetail;
        let steps = [];
        if (pipeline && pipeline.pipelineId === this.state.formData.pipelineId) {
            steps = (pipeline.steps || []).filter(s => s.enabled !== false).sort((a, b) => a.order - b.order);
        } else if (view && view.pipelineStepsSnapshot) {
            steps = (view.pipelineStepsSnapshot || []).filter(s => s.enabled !== false).sort((a, b) => a.order - b.order);
        }

        const sourceColumnNames = sourceFields.map(f => f.name);
        const effectiveColumnNames = steps.length > 0
            ? deriveColumnsAtStep(sourceColumnNames, steps, steps.length)
            : sourceColumnNames;

        // Build effective field list. Original fields keep their fieldId; derived columns
        // use the column name as fieldId (matches backend TransformExecutor behaviour).
        const effectiveFields = effectiveColumnNames.map(name => {
            const src = nameToSourceField[name];
            if (src) {
                return { fieldId: src.fieldId, name: src.name, derived: false };
            }
            return { fieldId: name, name: name, derived: true };
        });

        const allFieldIds = effectiveFields.map(f => f.fieldId);
        this.setState((prev) => {
            const hasExistingConfig = prev.formData.columnConfig.visible.length > 0
                || prev.formData.columnConfig.hidden.length > 0;

            let columnConfig;
            if (hasExistingConfig) {
                // Keep user's choices, but drop stale ids and add newly appeared fields to visible
                const validIds = new Set(allFieldIds);
                const visible = prev.formData.columnConfig.visible.filter(id => validIds.has(id));
                const hidden = prev.formData.columnConfig.hidden.filter(id => validIds.has(id));
                const known = new Set([...visible, ...hidden]);
                const added = allFieldIds.filter(id => !known.has(id));
                columnConfig = { visible: [...visible, ...added], hidden };
            } else {
                columnConfig = { visible: allFieldIds, hidden: [] };
            }

            return {
                schemaFields: effectiveFields,
                formData: { ...prev.formData, columnConfig },
            };
        });
    };

    onToggleField = (fieldId, checked) => {
        this.setState((prev) => {
            const { visible, hidden } = prev.formData.columnConfig;
            let newVisible, newHidden;
            if (checked) {
                newVisible = [...visible, fieldId];
                newHidden = hidden.filter(id => id !== fieldId);
            } else {
                newVisible = visible.filter(id => id !== fieldId);
                newHidden = [...hidden, fieldId];
            }
            return {
                formData: {
                    ...prev.formData,
                    columnConfig: { visible: newVisible, hidden: newHidden },
                },
            };
        });
    };

    onSelectAllFields = (checked) => {
        const allFieldIds = this.state.schemaFields.map(f => f.fieldId);
        this.setState((prev) => ({
            formData: {
                ...prev.formData,
                columnConfig: {
                    visible: checked ? allFieldIds : [],
                    hidden: checked ? [] : allFieldIds,
                },
            },
        }));
    };

    render() {
        const { loading, saving, isEdit, formData } = this.state;

        return (
            <Spin spinning={loading}>
                <div className="flex flex-col h-full">
                    {/* Breadcrumbs */}
                    <div className="px-4 py-2 border-b border-gray-200">
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <HomeOutlined /> Home
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <a onClick={this.onBack}>Views</a>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>{isEdit ? "Edit View" : this.state.isDuplicate ? "Duplicate View" : "Create View"}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>

                    {/* Header */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <Button icon={<ArrowLeftOutlined />} size="small" onClick={this.onBack} />
                            <h1 className="text-lg font-semibold m-0">{isEdit ? "Edit View" : this.state.isDuplicate ? "Duplicate View" : "Create View"}</h1>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="flex-1 overflow-auto p-4" style={{ maxWidth: 600 }}>
                        <Form layout="vertical">
                            <Form.Item label="View Name" required>
                                <Input
                                    value={formData.viewName}
                                    onChange={(e) => this.onFieldChange("viewName", e.target.value)}
                                    placeholder="Enter view name"
                                    maxLength={200}
                                />
                            </Form.Item>

                            <Form.Item label="Description">
                                <TextArea
                                    value={formData.description}
                                    onChange={(e) => this.onFieldChange("description", e.target.value)}
                                    placeholder="Enter description"
                                    maxLength={500}
                                    rows={3}
                                />
                            </Form.Item>

                            {!isEdit && !this.state.isDuplicate && (
                                <>
                                    <Form.Item label="Source Dataset ID" required>
                                        <Input
                                            value={formData.sourceDatasetId}
                                            onChange={(e) => this.onFieldChange("sourceDatasetId", e.target.value)}
                                            placeholder="Enter source dataset ID"
                                        />
                                    </Form.Item>

                                    <Form.Item label="Source Schema ID" required>
                                        <Input
                                            value={formData.sourceSchemaId}
                                            onChange={(e) => this.onFieldChange("sourceSchemaId", e.target.value)}
                                            placeholder="Enter source schema ID"
                                        />
                                    </Form.Item>

                                    <Form.Item label="Business Area ID" required>
                                        <Input
                                            value={formData.businessAreaId}
                                            onChange={(e) => this.onFieldChange("businessAreaId", e.target.value)}
                                            placeholder="Enter business area ID"
                                        />
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item label="Pipeline ID">
                                <Input
                                    value={formData.pipelineId || ""}
                                    onChange={(e) => this.onFieldChange("pipelineId", e.target.value || null)}
                                    placeholder="Enter pipeline ID (leave empty for column projection only)"
                                    disabled={this.state.isDuplicate || isEdit}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Column Configuration"
                                required
                                help={this.state.schemaFields.length === 0 && !this.state.loadingSchema && formData.sourceSchemaId
                                    ? "Enter a valid Schema ID and Business Area ID to load fields"
                                    : undefined}
                            >
                                {this.state.loadingSchema ? (
                                    <Spin size="small" />
                                ) : this.state.schemaFields.length > 0 ? (
                                    <div style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: 12, maxHeight: 300, overflowY: "auto" }}>
                                        <div style={{ marginBottom: 8, borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
                                            <Checkbox
                                                checked={formData.columnConfig.visible.length === this.state.schemaFields.length}
                                                indeterminate={formData.columnConfig.visible.length > 0 && formData.columnConfig.visible.length < this.state.schemaFields.length}
                                                onChange={(e) => this.onSelectAllFields(e.target.checked)}
                                            >
                                                Select All ({formData.columnConfig.visible.length} of {this.state.schemaFields.length} selected)
                                            </Checkbox>
                                        </div>
                                        {this.state.schemaFields.map((field) => (
                                            <div key={field.fieldId} style={{ padding: "4px 0" }}>
                                                <Checkbox
                                                    checked={formData.columnConfig.visible.includes(field.fieldId)}
                                                    onChange={(e) => this.onToggleField(field.fieldId, e.target.checked)}
                                                >
                                                    {field.name}
                                                </Checkbox>
                                            </div>
                                        ))}
                                    </div>
                                ) : !formData.sourceSchemaId ? (
                                    <Alert message="Select a source schema to configure columns" type="info" showIcon />
                                ) : null}
                            </Form.Item>

                            <Form.Item>
                                <div className="flex gap-2">
                                    <Button type="primary" onClick={this.onSave} loading={saving}>
                                        {isEdit ? "Save Changes" : this.state.isDuplicate ? "Duplicate View" : "Create View"}
                                    </Button>
                                    <Button onClick={this.onBack}>Cancel</Button>
                                </div>
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </Spin>
        );
    }
}

const mapStateToProps = (state) => ({
    tenant: state.userLogin.tenant,
    viewDetail: state.views.viewDetail,
    getViewByIdResult: state.views.getViewByIdResult,
    createViewResult: state.views.createViewResult,
    updateViewResult: state.views.updateViewResult,
    schemaModel: state.schemaModels.schemaModel,
    getSchemaModelResult: state.schemaModels.getSchemaModelResult,
    currentPipeline: state.pipelines.currentPipeline,
});

const mapDispatchToProps = (dispatch) => ({
    getViewById: (viewId) => dispatch(actions.getViewByIdRequest(viewId)),
    createView: (viewData) => dispatch(actions.createViewRequest(viewData)),
    updateView: (viewId, updateData) => dispatch(actions.updateViewRequest(viewId, updateData)),
    getSchemaModel: (businessAreaId, schemaId) => dispatch(actions.getSchemaModelRequest(businessAreaId, schemaId)),
    getPipelineById: (pipelineId) => dispatch(actions.getPipelineByIdRequest(pipelineId)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateEditViewPage));
